export const en = {
  // Navigation
  navigation: {
    features: 'Features',
    howItWorks: 'How it Works',
    templates: 'Templates',
    pricing: 'Pricing',
    faq: 'FAQ',
    getStartedFree: 'Get Started',
    back: 'Back',
    backGerman: 'Zurück',
    close: 'Close'
  },

  // Header and Navigation
  header: {
    saveFile: 'Save File',
    exportPdf: 'Export PDF',
    exporting: 'Processing Payment...',
    showEditorInputs: 'Show Editor Inputs',
    showSettings: 'Show Settings',
    typography: 'Typography',
    backupReminder: {
      title: 'Don\'t lose your work!',
      message: 'Save a free backup to continue later',
      dismiss: 'Got it',
      saveNow: 'Save Now'
    }
  },

  // Common Actions
  actions: {
    tip: 'Tip',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    open: 'Open',
    done: 'Done',
    confirm: 'Confirm',
    apply: 'Apply',
    reset: 'Reset',
    clear: 'Clear',
    clearAll: 'Clear All',
    upload: 'Upload',
    download: 'Download',
    import: 'Import',
    export: 'Export',
    preview: 'Preview',
    next: 'Next',
    previous: 'Previous',
    back: 'Back',
    continue: 'Continue',
    start: 'Start',
    finish: 'Finish',
    copy: 'Copy',
    paste: 'Paste',
    cut: 'Cut',
    undo: 'Undo',
    redo: 'Redo',
    select: 'Select',
    selectAll: 'Select All',
    none: 'None',
    all: 'All',
    loading: 'Loading...',
    processing: 'Processing...',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Information',
    required: 'Required',
    optional: 'Optional',
    show: 'Show',
    hide: 'Hide',
    expand: 'Expand',
    collapse: 'Collapse',
    minimize: 'Minimize',
    maximize: 'Maximize',
    refresh: 'Refresh',
    reload: 'Reload',
    retry: 'Retry',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    view: 'View',
    browse: 'Browse'
  },

  // Mobile Navigation
  mobile: {
    stylingOptions: 'Styling Options',
    importExport: 'Import/Export',
    saveAsJson: 'Save as JSON',
    loadFromJson: 'Load from JSON',
    selectTemplate: 'Select Template',
    changeLayout: 'Change Layout',
    changeDesign: 'Change Design',
    changeStyle: 'Change Style',
    changeBackground: 'Change Background',
    typographySettings: 'Typography Settings',
    language: 'Language',
    showEditorInputs: 'Show Editor Inputs',
    previewMode: 'Preview Mode',
    exportPdf: 'Export PDF',
    clearAllData: 'Clear All Data',
    editResume: 'Edit Resume',
    previewResume: 'Preview Resume',
    gestureHint: 'Pinch to zoom • Drag to move'
  },

  // Form sections
  sections: {
    personalInfo: 'Personal Information',
    professionalSummary: 'Professional Summary',
    workExperience: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certifications',
    awards: 'Awards and Honors',
    languages: 'Languages',
    volunteerExperience: 'Volunteer Experience',
    publications: 'Publications',
    references: 'References'
  },

  // Input section titles (for cards)
  inputSection: {
    sections: {
      'Personal Information': 'Personal Information',
      'Professional Summary': 'Professional Summary',
      'Work Experience': 'Work Experience',
      'Education': 'Education',
      'Skills': 'Skills',
      'Projects': 'Projects',
      'Certifications': 'Certifications',
      'Awards and Honors': 'Awards and Honors',
      'Languages': 'Languages',
      'Volunteer Experience': 'Volunteer Experience',
      'Publications': 'Publications',
      'References': 'References'
    },
    defaultTitles: {
      experience: 'Experience',
      education: 'Education',
      skill: 'Skill',
      project: 'Project',
      certification: 'Certification',
      award: 'Award',
      language: 'Language',
      volunteer: 'Volunteer Experience',
      publication: 'Publication',
      reference: 'Reference'
    }
  },

  // Layout names and categories
  layoutNames: {
    // Layout categories
    'Classic': 'Classic',
    'Sidebar': 'Sidebar', 
    'Topbar': 'Topbar',
    
    // Individual layout names
    'Centered Title': 'Centered Title',
    'Right-Aligned Title': 'Right-Aligned Title', 
    'Left-Aligned Title': 'Left-Aligned Title',
    'Sidebar Left': 'Sidebar Left',
    'Sidebar Right': 'Sidebar Right',
    
    // Layout descriptions
    descriptions: {
      'centered-header': 'Professional layout with centered header',
      'right-aligned-header': 'Modern layout with right-aligned header',
      'left-aligned-header': 'Modern layout with left-aligned header',
      'sidebar-left': 'Layout with skills and contact in left sidebar',
      'sidebar-right': 'Layout with skills and contact in right sidebar',
      'topbar-centered': 'Layout with skills and contact in a topbar aligned center',
      'topbar-right': 'Layout with skills and contact in a topbar aligned right',
      'topbar-left': 'Layout with skills and contact in a topbar aligned left'
    }
  },

  // Design names and categories  
  designNames: {
    // Design categories
    'default': 'Default',
    'tech': 'Tech',
    'professional': 'Professional',
    
    // Individual design names
    'default-black': 'Black',
    'default-gray': 'Gray',
    'blue': 'Blue',
    'green': 'Green',
    'orange': 'Orange',
    'colorful': 'Colorful',
    'gradient': 'Gradient',
    'vibrant-yellow': 'Vibrant Yellow',
    'navy-professional': 'Navy',
    'charcoal-professional': 'Charcoal',
    'beige-professional': 'Beige',
    'olive-professional': 'Olive',
    'teal-professional': 'Teal',
    'burgundy-professional': 'Burgundy'
  },

  // Template categories
  templateCategories: {
    'Classic': 'Classic',
    'Classic Centered Timeline': 'Classic Centered Timeline',
    'Sidebar Right Classic': 'Sidebar Right Classic',
    'Sidebar Left Classic': 'Sidebar Left Classic',
    'Sidebar Right Modern': 'Sidebar Right Modern',
    'Sidebar Left Modern': 'Sidebar Left Modern',
    'Topbar Classic': 'Topbar Classic',
    'Topbar Modern': 'Topbar Modern',
    'Tech': 'Tech',
    'Triangular': 'Triangular',
    'Geometric': 'Geometric',
    'Other': 'Other'
  },

  // Form components
  forms: {
    personalInfo: {
      title: 'Personal Information',
      profilePicture: 'Profile Picture',
      uploadPicture: 'Upload Picture',
      removePicture: 'Remove Picture',
      enablePictureSpace: 'Enable Picture Space',
      enablePictureSpaceHelper: 'Reserve space for profile picture even when no image is uploaded',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      linkedin: 'LinkedIn',
      github: 'GitHub',
      linkedinPlaceholder: 'https://linkedin.com/in/yourprofile',
      githubPlaceholder: 'https://github.com/yourusername'
    }
  },

  // Personal Information Fields
  personalInfo: {
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    linkedin: 'LinkedIn',
    github: 'GitHub',
    website: 'Website',
    uploadPicture: 'Upload Picture',
    removePicture: 'Remove Picture',
    enablePictureSpace: 'Enable Picture Space',
    enablePictureSpaceHelper: 'Reserve space for profile picture even when no image is uploaded',
    cropProfilePicture: 'Crop Profile Picture',
    dragToPosition: 'Drag to position your image within the circle',
    zoom: 'Zoom',
    dragToReposition: 'Drag to reposition'
  },

  // Work Experience Fields
  workExperience: {
    company: 'Company',
    jobTitle: 'Job Title',
    location: 'Location',
    startDate: 'Start Date',
    endDate: 'End Date',
    currentPosition: 'Current Position',
    description: 'Description',
    addExperience: 'Add Experience',
    experience: 'Experience'
  },

  // Education Fields
  education: {
    school: 'School',
    degree: 'Degree',
    fieldOfStudy: 'Field of Study',
    startDate: 'Start Date',
    endDate: 'End Date',
    description: 'Description',
    addEducation: 'Add Education',
    education: 'Education'
  },

  // Skills Fields
  skills: {
    displayAscategories: 'Display as Categories',
    displayAsIndividual: 'Display as Individual Skills',
    skillCategory: 'Skill Category',
    skillsCommaSeparated: 'Skills (comma separated)',
    skillName: 'Skill Name',
    proficiency: 'Proficiency',
    addSkill: 'Add Skill',
    skill: 'Skill',
    skillCategory: 'Skill Category',
    expert: 'Expert',
    advanced: 'Advanced',
    intermediate: 'Intermediate',
    basic: 'Basic',
    beginner: 'Beginner'
  },

  // Languages Fields
  languages: {
    language: 'Language',
    proficiency: 'Proficiency',
    addLanguage: 'Add Language',
    native: 'Native / Bilingual (C2)',
    fluent: 'Fluent (C1)',
    advanced: 'Advanced (B2)',
    intermediate: 'Intermediate (B1)',
    basic: 'Basic (A2)',
    beginner: 'Beginner (A1)'
  },

  // Projects Fields
  projects: {
    projectName: 'Project Name',
    technologiesUsed: 'Technologies Used',
    projectLink: 'Project Link',
    startDate: 'Start Date',
    endDate: 'End Date',
    description: 'Description',
    addProject: 'Add Project',
    project: 'Project'
  },

  // Certifications Fields
  certifications: {
    certificationName: 'Certification Name',
    issuingOrganization: 'Issuing Organization',
    issueDate: 'Issue Date',
    credentialId: 'Credential ID',
    credentialUrl: 'Credential URL',
    addCertification: 'Add Certification',
    certification: 'Certification'
  },

  // Awards Fields
  awards: {
    awardTitle: 'Award Title',
    issuer: 'Issuer',
    date: 'Date',
    description: 'Description',
    addAward: 'Add Award',
    award: 'Award'
  },

  // Volunteer Experience Fields
  volunteer: {
    organization: 'Organization',
    role: 'Role',
    startDate: 'Start Date',
    endDate: 'End Date',
    description: 'Description',
    addVolunteerExperience: 'Add Volunteer Experience',
    volunteerExperience: 'Volunteer Experience'
  },

  // Publications Fields
  publications: {
    publicationTitle: 'Publication Title',
    publisher: 'Publisher',
    publicationDate: 'Publication Date',
    url: 'URL',
    description: 'Description',
    addPublication: 'Add Publication',
    publication: 'Publication'
  },

  // References Fields
  references: {
    referenceName: 'Reference Name',
    position: 'Position',
    company: 'Company',
    email: 'Email',
    phone: 'Phone',
    addReference: 'Add Reference',
    reference: 'Reference'
  },

  // Settings
  settings: {
    layout: 'Layout',
    design: 'Design',
    typography: 'Typography',
    style: 'Style',
    background: 'Background',
    template: 'Template',
    specialEffects: 'Special Effects',
    barStyle: 'Bar Style',
    chooseLayout: 'Choose Layout',
    chooseDesign: 'Choose Design',
    chooseStyle: 'Choose Style',
    chooseBackground: 'Choose Background',
    applyTemplate: 'Apply Template',
    selectEffect: 'Select Effect',
    selectBarStyle: 'Select Bar Style'
  },

  // Layout categories
  layouts: {
    classic: 'Classic',
    modern: 'Modern',
    creative: 'Creative',
    professional: 'Professional',
    academic: 'Academic',
    technical: 'Technical',
    sidebar: 'Sidebar',
    topbar: 'Topbar',
    centered: 'Centered',
    left: 'Left Aligned',
    right: 'Right Aligned'
  },

  // Design categories
  designs: {
    professional: 'Professional',
    tech: 'Tech',
    creative: 'Creative',
    default: 'Default',
    navy: 'Navy',
    beige: 'Beige',
    burgundy: 'Burgundy',
    charcoal: 'Charcoal',
    olive: 'Olive',
    teal: 'Teal',
    blue: 'Blue',
    colorful: 'Colorful',
    gradient: 'Gradient',
    green: 'Green',
    orange: 'Orange',
    vibrantYellow: 'Vibrant Yellow',
    black: 'Black',
    gray: 'Gray'
  },

  // Background types
  backgrounds: {
    blank: 'Blank',
    tech: 'Tech Pattern',
    professional: 'Professional',
    geometric: 'Geometric',
    triangle: 'Triangle',
    rainbow: 'Rainbow Stripes',
    descriptions: {
      blank: 'Plain white background, no effects.',
      tech: 'Subtle animated pattern with tech-related icons.',
      professional: 'A clean and professional background with a focus on structure.',
      geometric: 'Subtle diagonal lines creating a geometric pattern.',
      triangle: 'Clean triangular shapes at top and bottom of the page.',
      rainbow: 'Colorful diagonal stripes in the top-right and bottom-left corners.'
    }
  },

  // Bar styles for proficiency visualization
  barStyles: {
    none: 'None',
    text: 'Textual',
    stars: 'Stars',
    bars: 'Bars',
    circles: 'Circles',
    squares: 'Squares',
    descriptions: {
      none: 'No visual proficiency indicator.',
      text: 'Show proficiency level as text (e.g., Intermediate).',
      stars: 'Render proficiency as a row of stars (1-5).',
      bars: 'Render proficiency as horizontal bars (1-5).',
      circles: 'Render proficiency as a row of circles (1-5).',
      squares: 'Render proficiency as a row of squares (1-5).'
    }
  },

  // Special effects
  specialEffects: {
    none: 'None',
    timeline: 'Timeline Effect',
    descriptions: {
      none: 'No special effects applied.',
      timeline: 'Adds a visual timeline connecting dated sections.'
    }
  },

  // Style names
  styles: {
    default: 'Default',
    tech: 'Tech',
    professional: 'Professional',
    blank: 'Blank',
    descriptions: {
      default: 'Clean look, no extra icons or borders.',
      tech: 'Modern tech-focused styling with icons.',
      professional: 'Professional styling with subtle borders.',
      blank: 'Minimal styling with no decorative elements.'
    }
  },

  // Typography settings
  typography: {
    fontFamily: 'Font Family',
    fontSize: 'Font Size',
    lineHeight: 'Line Height',
    global: 'Global Settings',
    headers: 'Headers',
    body: 'Body Text',
    small: 'Small Text',
    resetToDefaults: 'Reset to Defaults',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    extraLarge: 'Extra Large'
  },

  // File operations
  file: {
    selectFile: 'Select File',
    uploadFile: 'Upload File',
    importFromFile: 'Import from File',
    exportToFile: 'Export to File',
    supportedFormats: 'Supported formats',
    fileSize: 'File size',
    fileName: 'File name',
    invalidFile: 'Invalid file format',
    fileTooLarge: 'File too large',
    uploadError: 'Upload error',
    processing: 'Processing file...',
    parseError: 'Error parsing file'
  },

  // Welcome screen
  welcome: {
    title: 'Welcome to CV Builder',
    subtitle: 'Create your professional resume in minutes',
    startManual: 'Start from Scratch',
    uploadResume: 'Upload Existing Resume',
    importLinkedIn: 'Import from LinkedIn',
    importOptions: 'Import Options',
    getStarted: 'Get Started',
    chooseOption: 'Choose how you\'d like to begin'
  },

  // Preview and export
  preview: {
    livePreview: 'Live Preview',
    pageControls: 'Page Controls',
    zoomIn: 'Zoom In',
    zoomOut: 'Zoom Out',
    fitToWidth: 'Fit to Width',
    actualSize: 'Actual Size',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
    previousPage: 'Previous Page',
    nextPage: 'Next Page',
    page: 'Page',
    of: 'of'
  },

  // Validation messages
  validation: {
    required: 'This field is required',
    invalidEmail: 'Please enter a valid email address',
    invalidPhone: 'Please enter a valid phone number',
    invalidUrl: 'Please enter a valid URL',
    tooShort: 'This field is too short',
    tooLong: 'This field is too long',
    invalidDate: 'Please enter a valid date',
    endDateBeforeStart: 'End date cannot be before start date'
  },

  // Error messages
  errors: {
    generic: 'An error occurred',
    networkError: 'Network error. Please check your connection.',
    fileNotFound: 'File not found',
    parseError: 'Error parsing data',
    exportError: 'Error exporting file',
    importError: 'Error importing file',
    saveFailed: 'Failed to save changes',
    loadFailed: 'Failed to load data',
    invalidData: 'Invalid data format',
    permissionDenied: 'Permission denied',
    timeout: 'Request timed out',
    serverError: 'Server error occurred'
  },

  // Success messages
  success: {
    saved: 'Successfully saved',
    exported: 'Successfully exported',
    imported: 'Successfully imported',
    updated: 'Successfully updated',
    deleted: 'Successfully deleted',
    copied: 'Copied to clipboard',
    reset: 'Reset to defaults'
  },

  // Date and time
  dateTime: {
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    present: 'Present',
    current: 'Current',
    ongoing: 'Ongoing',
    january: 'January',
    february: 'February',
    march: 'March',
    april: 'April',
    may: 'May',
    june: 'June',
    july: 'July',
    august: 'August',
    september: 'September',
    october: 'October',
    november: 'November',
    december: 'December'
  },

  // Hero Section
  hero: {
    badge: {
      madeInEu: 'Made in EU',
      privateSecure: '100% Private'
    },
    headline: 'Create Professional Résumés That Get You Hired',
    highlightWord: 'Professional Résumés',
    subtitle: 'Stunning templates. Effortless formatting. Total privacy. Create your résumé in the browser—no sign-up, no subscription',
    features: {
      alwaysPrivate: 'Always Private',
      readyInMinutes: 'Ready in Minutes',
      beautifulTemplates: 'Beautiful Templates'
    },
    cta: {
      primary: 'Start Building',
      secondary: 'How It Works'
    }
  },

  // Features Section
  features: {
    sectionTitle: 'THE CLEAR CHOICE',
    title: 'Why choose privacv?',
    subtitle: 'The modern resume builder that respects your privacy, saves your money, and values your time.',
    
    advantages: {
      payPerUse: {
        title: 'Pay Per Use',
        privacv: 'From €0.99 - No subscriptions',
        others: '$15-50 monthly subscriptions',
        highlight: 'Multiple options: one-time or time passes'
      },
      zeroDataCollection: {
        title: 'Zero Data Collection',
        privacv: 'Data never leaves your browser',
        others: 'Collects & stores personal info',
        highlight: 'Impossible to breach your data'
      },
      instantAccess: {
        title: 'Instant Access',
        privacv: 'Start building immediately',
        others: 'Email verification & approvals',
        highlight: 'No signup friction or delays'
      },
      noRegistration: {
        title: 'No Registration',
        privacv: 'Completely anonymous',
        others: 'Mandatory account creation',
        highlight: 'No passwords or profiles'
      }
    },

    additionalBenefits: {
      title: 'Beyond the basics — even more advantages',
      professionalQuality: {
        title: 'Professional Quality',
        description: 'ATS-optimized templates designed by experts',
        value: 'Stand out to recruiters and pass automated screening'
      },
      fullFeatureAccess: {
        title: 'Full Feature Access',
        description: 'Everything unlocked from day one',
        value: 'No premium tiers or feature restrictions'
      },
      offlineCapable: {
        title: 'Offline Capable',
        description: 'Works without internet connection',
        value: 'Build your resume anywhere, anytime'
      },
      noVendorLockIn: {
        title: 'No Vendor Lock-in',
        description: 'Export your data anytime',
        value: 'Complete control over your information'
      },
      gdprCompliant: {
        title: 'GDPR Compliant',
        description: 'Privacy by design architecture',
        value: 'Legal compliance without the complexity'
      },
      noSpamEver: {
        title: 'No Spam Ever',
        description: 'Zero marketing emails or tracking',
        value: 'Peaceful, distraction-free experience'
      }
    }
  },

  // How It Works Section
  howItWorks: {
    title: 'How It Works',
    subtitle: 'Create a professional resume in just 3 simple steps.',
    steps: {
      addInfo: {
        title: 'Add Your Information',
        description: 'Fill in your details or import from LinkedIn, PDF, or DOCX.'
      },
      chooseStyle: {
        title: 'Choose Your Style',
        description: 'Select from professional templates and customize to match your style.'
      },
      download: {
        title: 'Download & Apply',
        description: 'Export your polished resume and apply with confidence.'
      }
    },
    cta: {
      title: 'Ready to get started?',
      subtitle: 'Join thousands of professionals who have already created their perfect resume',
      button: 'Start Building'
    },
    hint: {
      title: 'Pro Tip:',
      description: 'Download the free CV save file to continue where you left off anytime'
    }
  },

  // Templates Section
  templates: {
    title: 'Professional Templates by Industry',
    subtitle: 'Explore 25 carefully curated templates representing every industry and style. Each design is crafted to highlight the skills and experience that matter most in your profession.',
    stats: {
      templates: 'Templates',
      customization: 'Customization',
      industries: 'Industries',
      atsCompatible: 'ATS Compatible'
    },
    popular: 'Popular',
    useTemplate: 'Use Template',
    descriptions: {
      softwareEngineer: 'Modern tech-focused template ideal for developers, programmers, and IT professionals',
      accountExecutive: 'Executive-focused topbar design perfect for senior sales and business development roles',
      healthcareProfessional: 'Clean, trustworthy design perfect for medical professionals, nurses, and healthcare administrators',
      marketingSpecialist: 'Creative geometric design perfect for marketing, advertising, and brand professionals',
      environmentalScientist: 'Modern topbar layout ideal for sustainability consultants and environmental professionals',
      financialAdvisor: 'Conservative, professional design for banking, finance, and investment professionals',
      civilEngineer: 'Technical, precise design perfect for engineers, architects, and technical professionals',
      managementConsultant: 'Clean topbar design with light theme perfect for consulting and strategic advisory roles',
      legalCounsel: 'Traditional, authoritative template for lawyers, paralegals, and legal professionals',
      creativeDirector: 'Bold, artistic design for designers, artists, and creative industry professionals',
      educationLeader: 'Scholarly, clean design for teachers, professors, and educational administrators',
      salesExecutive: 'Dynamic, persuasive template designed for sales professionals and account managers',
      corporateExecutive: 'Professional topbar layout ideal for C-suite executives and senior corporate leadership',
      hrSpecialist: 'Warm, approachable design ideal for human resources and people operations professionals',
      businessConsultant: 'Professional consulting template with structured timeline for strategic roles',
      productManager: 'Modern topbar design perfect for product managers and innovation leaders',
      operationsManager: 'Organized, efficient layout perfect for operations, logistics, and management roles',
      realEstateAgent: 'Trustworthy, professional template for real estate agents and property professionals',
      startupFounder: 'Bold, innovative template perfect for entrepreneurs and startup professionals',
      hospitalityManager: 'Welcoming, friendly design for hospitality, tourism, and customer service professionals',
      researchScientist: 'Clean, analytical design perfect for researchers, scientists, and academic professionals',
      retailManager: 'Bright, approachable design for retail, customer service, and front-facing roles',
      nonprofitDirector: 'Mission-driven, compassionate design for nonprofit and social impact professionals',
      projectManager: 'Structured, organized template ideal for project managers and process improvement roles',
      executiveLeader: 'Premium, sophisticated design for C-suite executives and senior leadership positions'
    },
    finalCta: {
      title: 'Don\'t see your industry? No problem!',
      subtitle: 'Every template is fully customizable. Start with any design and make it your own.',
      button: 'Browse All Templates'
    }
  },

  // Pricing Section
  pricing: {
    title: 'Simple, Transparent Pricing',
    subtitle: 'Build for free, pay only when you download. No subscriptions, no hidden fees.',
    mostPopular: 'Most Popular',
    price: '€4.99',
    priceSubtitle: 'One-time payment',
    priceRange: '€4.99-19.99',
    features: {
      unlimitedResumes: 'Unlimited resume building',
      allTemplates: 'All professional templates',
      privacyProtection: 'Complete privacy protection',
      atsOptimized: 'ATS-optimized design',
      noSignup: 'No signup or credit card to start',
      payPerDownload: 'Pay only when you download PDF'
    },
    cta: 'Start Building',
    passFeatures: {
      singleDownload: 'Single PDF download',
      unlimitedDownloads24h: 'Unlimited downloads for 24 hours',
      unlimitedDownloads7d: 'Unlimited downloads for 7 days',
      unlimitedDownloads30d: 'Unlimited downloads for 30 days',
      allTemplates: 'All professional templates',
      fullCustomization: 'Full customization options',
      perfectForSingle: 'Perfect for single application',
      greatForRevisions: 'Great for quick revisions',
      perfectForJobHunt: 'Perfect for active job hunting',
      bestValue: 'Best value for extended use'
    },
    whyTimeLimited: {
      title: 'Why time-limited passes instead of subscriptions?',
      description: 'Job hunting is intensive but temporary. Our passes give you unlimited downloads during your active search period, then automatically end—no recurring charges, no forgotten subscriptions. Choose the duration that fits your timeline.'
    }
  },

  // FAQ Section
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'Everything you need to know about privacv. Can\'t find what you\'re looking for? Just start using the app!',
    showMore: 'Show More Questions',
    showLess: 'Show Less',
    questions: {
      howMuchCost: {
        question: 'How much does privacv cost?',
        answer: 'privacv is free to use for building and editing your resume. You only pay €4.99 when you want to download the final PDF. No subscriptions, no monthly fees, no hidden costs. Build unlimited resumes and pay only when you need the professional PDF output.'
      },
      howProtectPrivacy: {
        question: 'How does privacv protect my privacy?',
        answer: 'Your data never leaves your browser. Everything is processed locally on your device, which means we never see, store, or track your personal information. No accounts are required, no data is collected, and no tracking cookies are used.'
      },
      atsFriendly: {
        question: 'Are the resumes ATS-friendly?',
        answer: 'Absolutely! All our templates are designed to be ATS (Applicant Tracking System) compatible. They use proper formatting, standard fonts, and clear structure that ATS systems can easily parse and understand.'
      },
      canImport: {
        question: 'Can I import my existing resume or LinkedIn profile?',
        answer: 'Yes! You can import data from multiple sources including LinkedIn profiles (ZIP export), existing resumes in PDF or DOCX format. Our smart parser will extract the information and populate your resume automatically.'
      },
      exportFormats: {
        question: 'What file formats can I export to?',
        answer: 'You can export your resume as a high-quality PDF for €4.99, perfect for job applications with professional formatting. You can also save your data as JSON for free to backup and continue editing later.'
      },
      needAccount: {
        question: 'Do I need to create an account?',
        answer: 'No account required! You can start building your resume immediately without any registration, passwords, or personal information. Just visit the site and begin creating.'
      },
      mobileDevices: {
        question: 'Can I use privacv on mobile devices?',
        answer: 'Yes! privacv is fully responsive and works great on all devices including smartphones and tablets. You can create and edit your resume on the go with the same features as the desktop version.'
      },
      howManyResumes: {
        question: 'How many resumes can I create?',
        answer: 'You can build unlimited resumes for free! Create as many as you need for different job applications, industries, or career stages. Each resume can be customized independently with different templates and content. You only pay when you download the final PDF.'
      },
      canCustomize: {
        question: 'Can I customize the templates?',
        answer: 'Absolutely! You can customize colors, fonts, layouts, and sections to match your personal style and industry requirements. All templates are fully customizable while maintaining their professional appearance.'
      },
      needHelp: {
        question: 'What if I need help or have questions?',
        answer: 'While privacv is designed to be intuitive and user-friendly, we provide comprehensive guides and tips throughout the application. The interface includes helpful hints and best practices for each section.'
      }
    },
    finalCta: {
      title: 'Still have questions?',
      subtitle: 'The best way to get answers is to try privacv yourself. Start building for free and see how easy it is!'
    }
  },

  // Testimonials Section
  testimonials: {
    title: 'Loved by Professionals',
    subtitle: 'Join thousands of professionals who have successfully landed their dream jobs using privacv',
    stats: {
      resumesCreated: 'Resumes Created',
      averageRating: 'Average Rating',
      successRate: 'Success Rate'
    },
    quotes: {
      sarahJohnson: 'privacv helped me land my dream job. The privacy-first approach gave me peace of mind.',
      michaelChen: 'The best resume builder I\'ve used. Clean interface, beautiful templates, and no data tracking. Highly recommended!',
      emilyRodriguez: 'As a designer, I appreciate the attention to detail in the templates. The customization options are endless.',
      davidKim: 'Built my resume in under 30 minutes. The ATS optimization features helped me get past the initial screening.',
      lisaThompson: 'The privacy aspect is what sold me. No accounts, no tracking, just a great tool that respects your data.',
      jamesWilson: 'Imported my profile seamlessly and had a polished resume ready in minutes. Game changer!'
    }
  },

  // Footer Section
  footer: {
    getStarted: 'Get Started',
    legal: {
      impressum: 'Impressum',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy'
    },
    tagline: 'Build your perfect resume with complete privacy',
    description: 'The privacy-first resume builder that helps you create professional resumes without compromising your personal data. Built by professionals, for professionals.',
    features: {
      privateSecure: '100% Private',
      readyInMinutes: 'Ready in Minutes',
      professionalTemplates: 'Professional Templates'
    },
    madeWith: 'Made with',
    forJobSeekers: 'for job seekers everywhere',
    copyright: 'privacv. Built with privacy in mind. No data collection, no tracking.',
    cta: {
      title: 'Ready to create your professional resume?',
      subtitle: 'Join thousands of professionals who trust privacv',
      button: 'Start Building'
    }
  },

  // Language Selection
  language: {
    select: 'Language',
    english: 'English',
    german: 'Deutsch',
    french: 'Français'
  },

  // Welcome Screen
  welcome: {
    title: 'Let\'s Build Your Resume',
    subtitle: 'Choose how you\'d like to get started. You can import existing data or start fresh with manual entry.',
    trustIndicators: {
      private: '100% Private',
      fast: 'Ready in Minutes'
    },
    startFromScratch: {
      title: 'Start from Scratch',
      description: 'Enter your information manually with our guided form. Perfect for creating a fresh, customized resume.',
      getStarted: 'Get Started'
    },
    orImport: 'OR IMPORT EXISTING DATA',
    importOptions: {
      title: 'Import Options',
      linkedin: {
        title: 'Import from LinkedIn',
        description: 'Upload your LinkedIn data export (ZIP file)',
        badge: 'Popular',
        howToTitle: 'How to get your LinkedIn data:',
        step1: 'Go to LinkedIn Settings & Privacy.',
        step2: 'Click "Data Privacy" → "Get a copy of your data".',
        step3: 'Select the first option.',
        step4: 'Download the ZIP file you receive via email and upload it here.',
        uploadButton: 'Upload ZIP File',
        openLinkedIn: 'Open LinkedIn'
      },
      personas: {
        title: 'Pre-load Examples',
        description: 'Choose from professional CV templates across different industries',
        badge: 'Popular'
      },
      json: {
        title: 'Import Previous Resume',
        description: 'Load a previously saved resume (JSON file)'
      },
      docx: {
        title: 'Import Word Document',
        description: 'Upload an existing resume (DOCX file) - Enhanced parsing with structure detection',
        badge: 'Coming Soon'
      },
      pdf: {
        title: 'Import PDF Resume',
        description: 'Extract text from a PDF resume - Improved layout-aware parsing',
        badge: 'Coming Soon'
      }
    },
    processing: {
      title: 'Processing your data...',
      subtitle: 'This may take a few moments'
    },
    tip: 'LinkedIn exports provide the most comprehensive data. You can download your LinkedIn data from Settings & Privacy → Data Privacy → Get a copy of your data.'
  },

  // Input Section
  input: {
    backToStart: 'Back to Start',
    buildingResume: 'Building Your Resume',
    addSections: 'Add Sections'
  },

  // Settings Panel specific translations
  settingsPanel: {
    tabs: {
      templates: 'Templates',
      layout: 'Layout',
      design: 'Design',
      typography: 'Typography'
    },
    templates: {
      title: 'Select a Template',
      deleteConfirm: 'Are you sure you want to delete the template',
      noPreview: 'No Preview'
    },
    layout: {
      categories: {
        classic: 'Classic',
        'classic centered timeline': 'Classic Centered Timeline', 
        'sidebar right classic': 'Sidebar Right Classic',
        'sidebar left classic': 'Sidebar Left Classic',
        'sidebar right modern': 'Sidebar Right Modern',
        'sidebar left modern': 'Sidebar Left Modern',
        'topbar classic': 'Topbar Classic',
        'topbar modern': 'Topbar Modern',
        tech: 'Tech',
        triangular: 'Triangular',
        geometric: 'Geometric',
        other: 'Other'
      }
    },
    design: {
      colorSchemes: 'Color Schemes',
      categories: {
        default: 'Default',
        professional: 'Professional', 
        tech: 'Tech',
        other: 'Other',
        custom: 'Custom'
      },
      customControls: 'Customization Controls',
      baseDesign: 'Base Design',
      editColors: 'Edit Colors',
      saveScheme: 'Save Current Scheme',
      schemeName: 'Scheme Name',
      savedSchemes: 'Saved Custom Schemes',
      load: 'Load',
      editCustom: 'Edit Custom'
    },
    background: {
      title: 'Page Background Effect'
    },
    barStyle: {
      title: 'Proficiency Display Style'
    },
    specialEffects: {
      title: 'Special Effects'
    },
    style: {
      title: 'Appearance Style'
    },
    typography: {
      reset: 'Reset',
      sections: {
        cvTitle: 'CV Title (Name)',
        sectionTitle: 'Section Titles',
        body: 'Body Text',
        smallText: 'Small Text (Dates, etc.)'
      },
      fields: {
        size: 'Size (pt)',
        spacing: 'Spacing',
        fontFamily: 'Font Family'
      }
    }
  },

  // Image Cropper
  imageCropper: {
    title: 'Crop Profile Picture',
    instruction: 'Drag to position your image within the circle',
    zoom: 'Zoom',
    reposition: 'Drag to reposition',
    processing: 'Processing...',
    error: 'Failed to crop image. Please try again.',
    failedToLoad: 'Failed to load image. Please try a different file.',
    sizeTooLarge: 'Please select an image smaller than',
    invalidFile: 'Please select a valid image file.'
  },

  // Generic messages
  messages: {
    noItemsAvailable: 'No items available.',
    noBackgroundsAvailable: 'No backgrounds available.',
    noBarStylesAvailable: 'No bar styles available.',
    noSpecialEffectsAvailable: 'No special effects available.',
    noStylesAvailable: 'No styles available.'
  },

  // Special effects
  specialEffects: {
    'None': 'None',
    'Timeline Effect': 'Timeline Effect',
  },

  // Special effect descriptions
  specialEffectDescriptions: {
    'No special visual effects applied.': 'No special visual effects applied.',
    'Adds a visual timeline connecting dated sections.': 'Adds a visual timeline connecting dated sections.',
  },

  // Design color names
  designNames: {
    'Default Black': 'Default Black',
    'Default Gray': 'Default Gray',
    'Blue': 'Blue',
    'Green': 'Green',
    'Orange': 'Orange',
    'Colorful': 'Colorful',
    'Social': 'Social',
    'Yellow': 'Vibrant Yellow', // Backward compatibility
    'Vibrant Yellow': 'Vibrant Yellow',
    'Navy': 'Navy',
    'Charcoal': 'Charcoal',
    'Beige': 'Beige',
    'Olive': 'Olive',
    'Teal': 'Teal',
    'Burgundy': 'Burgundy',
  },

  // Payment Modal
  payment: {
    modal: {
      title: 'PDF Download',
      subtitle: 'Choose your option',
      chooseOption: 'Choose your payment option',
      tabs: {
        code: 'Have a Code',
        purchase: 'New Purchase'
      },
      explanatoryText: 'Time-limited passes allow unlimited downloads during their validity period.',
      securePayment: 'Secure Payment',
      redirectMessage: 'You\'ll be redirected to Stripe\'s secure checkout to complete your payment',
      securityNotice: 'Secured by Stripe • Your payment information is encrypted and secure',
      cancel: 'Cancel',
      cancelButton: 'Cancel',
      proceedButton: 'Proceed to Payment',
      redirecting: 'Redirecting...',
      error: 'Please select a payment option to continue.',
      selectOption: 'Select an option to continue'
    },
    tabs: {
      existingCustomer: 'Have a Download Key',
      newPurchase: 'New Purchase'
    },
    codeInput: {
      title: 'Enter Download Key',
      description: 'Have a download key? Enter it below',
      placeholder: 'Enter your download key',
      submit: 'Verify Key',
      verifying: 'Verifying...',
      hint: 'Download keys are provided with 24h, week, and month passes',
      errors: {
        invalid: 'Invalid download key format',
        expired: 'This download key has expired',
        notFound: 'Download key not found',
        generic: 'Failed to verify download key'
      }
    },
    products: {
      pdf_one_time: {
        name: 'One-Time Download',
        description: 'Single PDF export'
      },
      pdf_24h: {
        name: '24 Hour Pass',
        description: 'Unlimited downloads for 24h'
      },
      pdf_week: {
        name: 'Week Pass',
        description: 'Unlimited downloads for 7 days'
      },
      pdf_month: {
        name: 'Month Pass',
        description: 'Unlimited downloads for 30 days'
      }
    },
    option: {
      emailDelivery: 'Download key sent via email'
    },
    errors: {
      failedToCreate: 'Failed to create checkout session',
      stripeNotConfigured: 'Stripe not configured',
      paymentFailed: 'Payment failed. Please try again.',
      noProductSelected: 'Please select a payment option to continue.'
    }
  },

  // Legal Pages
  legal: {
    // Common elements
    common: {
      lastUpdated: 'Last updated: {date}',
      backButton: 'Back',
      contactEmail: 'info@privacv.com',
      companyName: 'Navid Sadat-Razavi',
      companyAddress: 'Kopischstr. 1',
      companyCity: '10965 Berlin',
      companyCountry: 'Germany',
      pdfPrice: '€4.99',
              pdfPriceRange: '€0.99-7.99',
      currency: '€',
      websiteUrl: 'privacv.com'
    },

    // Privacy Policy
    privacy: {
      title: 'Privacy Policy',
      sections: {
        overview: {
          title: '1. Privacy at a Glance',
          generalInfo: {
            title: 'General Information',
            content: 'The following information provides a simple overview of what happens to your personal data when you visit this website. Personal data is any data that can personally identify you.'
          },
          dataCollection: {
            title: 'Data Collection on This Website',
            whoResponsible: {
              question: 'Who is responsible for data collection on this website?',
              answer: 'Data processing on this website is carried out by the website operator. You can find their contact details in the imprint of this website.'
            },
            howCollect: {
              question: 'How do we collect your data?',
              answer: 'Your data is collected when you provide it to us. This could be data you enter in a contact form. Other data is collected automatically by our IT systems when you visit the website. This is mainly technical data (e.g. internet browser, operating system, or time of page access).'
            },
            whatFor: {
              question: 'What do we use your data for?',
              answer: 'Part of the data is collected to ensure error-free provision of the website. Other data may be used to analyze your user behavior.'
            },
            yourRights: {
              question: 'What rights do you have regarding your data?',
              answer: 'You always have the right to receive information free of charge about the origin, recipient and purpose of your stored personal data. You also have the right to request correction or deletion of this data.'
            }
          }
        },
        hosting: {
          title: '2. Hosting and Content Delivery Networks (CDN)',
          externalHosting: {
            title: 'External Hosting',
            content: 'This website is hosted by an external service provider (host). The personal data collected on this website is stored on the host\'s servers. This may include IP addresses, contact requests, meta and communication data, contract data, contact details, names, website accesses and other data generated via a website.\n\nExternal hosting is carried out for the purpose of contract fulfillment towards our potential and existing customers (Art. 6 para. 1 lit. b GDPR) and in the interest of secure, fast and efficient provision of our online offer by a professional provider (Art. 6 para. 1 lit. f GDPR).'
          }
        },
        generalInfo: {
          title: '3. General Information and Mandatory Information',
          dataProtection: {
            title: 'Data Protection',
            content: 'The operators of these pages take the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with legal data protection regulations and this privacy policy.'
          },
          responsibleParty: {
            title: 'Information about the Responsible Party',
            content: 'The responsible party for data processing on this website is:\n\n{companyName}\n{companyAddress}\n{companyCity}\n{companyCountry}\n\nEmail: {contactEmail}'
          },
          revocation: {
            title: 'Revocation of Your Consent to Data Processing',
            content: 'Many data processing operations are only possible with your express consent. You can revoke consent you have already given at any time. An informal notification by email is sufficient. The legality of data processing carried out until revocation remains unaffected by the revocation.'
          },
          complaint: {
            title: 'Right to Complain to the Competent Supervisory Authority',
            content: 'In the event of violations of the GDPR, data subjects have the right to complain to a supervisory authority, particularly in the member state of their habitual residence, their place of work, or the place of the alleged violation. The right to complain exists without prejudice to other administrative or judicial remedies.'
          },
          portability: {
            title: 'Right to Data Portability',
            content: 'You have the right to have data that we process automatically based on your consent or in fulfillment of a contract handed over to you or to a third party in a common, machine-readable format. If you request direct transfer of the data to another responsible party, this will only be done to the extent that it is technically feasible.'
          }
        },
        dataCollection: {
          title: '4. Data Collection on This Website',
          cookies: {
            title: 'Cookies',
            content: 'Our websites use so-called "cookies". Cookies are small text files and do not cause any damage to your device. They are stored either temporarily for the duration of a session (session cookies) or permanently (permanent cookies) on your device.'
          },
          serverLogs: {
            title: 'Server Log Files',
            content: 'The page provider automatically collects and stores information in so-called server log files, which your browser automatically transmits to us. These are:\n\n• Browser type and browser version\n• Operating system used\n• Referrer URL\n• Hostname of the accessing computer\n• Time of server request\n• IP address\n\nThis data is not combined with other data sources. Data collection is based on Art. 6 para. 1 lit. f GDPR. The website operator has a legitimate interest in the technically error-free presentation and optimization of its website – server log files must be recorded for this purpose.'
          },
          localStorage: {
            title: 'Local Data Storage',
            content: 'privacv is a privacy-first application that performs all data processing locally in your browser. This means:\n\n• Your resume data is only stored in your browser\'s local storage\n• No data transmission to our servers during creation and editing\n• You have complete control over your data\n• Data can be removed at any time by clearing browser storage'
          },
          stripePayment: {
            title: 'Payment Processing with Stripe',
            content: 'For our time-limited pass service, we use Stripe as our payment processor. During payment, the following data is transmitted to Stripe:\n\n• Payment information (credit card, etc.)\n• Transaction amount ({pdfPriceRange})\n• Transaction timestamp\n• No personal data from your resume\n\n**Important:** Your resume data is never transmitted to Stripe or other third parties. Only payment information is processed for transaction handling.\n\n**Legal basis:** Data processing is based on Art. 6 para. 1 lit. b GDPR (contract fulfillment) for payment transaction processing.\n\nFor more information about Stripe\'s privacy practices, visit: https://stripe.com/privacy'
          },
          analytics: {
            title: 'Website Analytics with Umami',
            content: 'This website uses Umami, a privacy-friendly web analytics service. Umami only collects anonymized usage data to analyze and improve website performance.\n\n**Data collected:**\n• Page views and visitor counts\n• Referring websites (referrer)\n• Browser and device information\n• Geographic region (country/state)\n• Visit duration and interactions\n\n**Privacy with Umami:**\n• No IP address storage\n• No cookies or tracking\n• No personal data\n• Server location: EU\n• GDPR compliant\n\n**Legal basis:** Data processing is based on Art. 6 para. 1 lit. f GDPR (legitimate interest) for website analysis and improvement.\n\n**Opt-out:** You can disable Umami tracking at any time by enabling the "Do Not Track" setting in your browser.\n\n**Technical safeguards:** Umami processes data exclusively pseudonymously, without cross-site tracking and with automatic data deletion after 24 months. IP addresses are shortened and no profiling for advertising purposes takes place.'
          }
        }
      }
    },

    // Terms of Service
    terms: {
      title: 'Terms of Service',
      sections: {
        scope: {
          title: '1. Scope',
          content: 'These terms of use apply to the use of the website {websiteUrl} and the services provided through it. By using the website, you agree to these terms.'
        },
        serviceDescription: {
          title: '2. Service Description',
          content: 'privacv is an online tool for creating resumes that works entirely in the user\'s browser. Creating and editing resumes is free. Time-limited passes ({pdfPriceRange}) are available for PDF downloads, providing unlimited downloads during the pass validity period. All data processing is done locally without transferring personal data to our servers, except for payment processing via Stripe.'
        },
        rightsAndPricing: {
          title: '3. Usage Rights and Pricing',
          content: 'Creating and editing resumes with privacv is free. You receive the non-exclusive right to use the service for private and commercial purposes. Time-limited passes ({pdfPriceRange}) are available for unlimited PDF downloads during the pass validity period (24 hours, 7 days, or 30 days).\n\nRedistribution or commercial use of the website content is prohibited without express permission. You may freely use downloaded PDFs for application purposes.'
        },
        paymentTerms: {
          title: '4. Payment Terms',
          content: 'Payment for time-limited passes is processed through our payment service provider Stripe. Pass prices ({pdfPriceRange}) are due immediately upon successful payment. After successful payment, unlimited PDF downloads are available for the selected time period.\n\nAll prices include statutory VAT. Passes automatically expire after their validity period with no recurring charges.'
        },
        userObligations: {
          title: '5. User Obligations',
          content: 'The user undertakes to use the service only for lawful purposes. In particular, it is prohibited to:\n\n• Enter false or misleading information in resumes\n• Use the service for illegal activities\n• Circumvent technical protection measures\n• Overload or damage the website'
        },
        dataProtection: {
          title: '6. Data Protection',
          content: 'Protecting your data is our highest priority. All your inputs are stored and processed exclusively locally in your browser. No transmission of your personal data to our servers takes place, except for payment processing via Stripe, where only payment information is transmitted. For more information, see our privacy policy.'
        },
        availability: {
          title: '7. Service Availability',
          content: 'We strive for high availability of the service, but cannot guarantee guaranteed availability. Maintenance work and technical disruptions may lead to temporary outages.'
        },
        liability: {
          title: '8. Liability',
          content: 'Use of privacv is at your own risk. We are only liable for damages that are due to intentional or grossly negligent behavior on our part. Liability for indirect damages, lost profits, or data loss is excluded to the extent legally permissible.'
        },
        copyright: {
          title: '9. Copyright',
          content: 'All content of the website, including designs, texts, and software, is protected by copyright. The resume templates may only be used for creating personal resumes.'
        },
        changes: {
          title: '10. Changes to Terms of Service',
          content: 'We reserve the right to change these terms of service at any time. Changes will be published on the website and take effect upon publication.'
        },
        finalProvisions: {
          title: '11. Final Provisions',
          content: 'German law applies, excluding the UN Convention on Contracts for the International Sale of Goods. The place of jurisdiction is the seat of our company. Should individual provisions of these terms of service be invalid, this does not affect the validity of the remaining provisions.'
        }
      }
    },

    // Imprint
    imprint: {
      title: 'Imprint',
      sections: {
        tmgInfo: {
          title: 'Information according to § 5 TMG',
          content: '{companyName}\n{companyAddress}\n{companyCity}\n{companyCountry}'
        },
        contact: {
          title: 'Contact',
          content: 'Email: {contactEmail}'
        },
        responsibleContent: {
          title: 'Responsible for content according to § 55 para. 2 RStV',
          content: '{companyName}\n{companyAddress}\n{companyCity}'
        },
        contentLiability: {
          title: 'Liability for Content',
          content: 'As a service provider, we are responsible for our own content on these pages according to § 7 para. 1 TMG under general laws. According to §§ 8 to 10 TMG, however, we are not obligated as a service provider to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.\n\nObligations to remove or block the use of information under general laws remain unaffected. However, liability in this regard is only possible from the time of knowledge of a specific legal violation. Upon becoming aware of corresponding legal violations, we will remove this content immediately.'
        },
        linkLiability: {
          title: 'Liability for Links',
          content: 'Our offer contains links to external websites of third parties, on whose contents we have no influence. Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the pages is always responsible for the contents of the linked pages. The linked pages were checked for possible legal violations at the time of linking. Illegal contents were not recognizable at the time of linking.\n\nHowever, permanent content control of the linked pages is not reasonable without concrete evidence of a legal violation. Upon becoming aware of legal violations, we will remove such links immediately.'
        },
        copyright: {
          title: 'Copyright',
          content: 'The content and works created by the site operators on these pages are subject to German copyright law. Duplication, processing, distribution and any kind of exploitation outside the limits of copyright require the written consent of the respective author or creator. Downloads and copies of this page are only permitted for private, non-commercial use.\n\nInsofar as the content on this site was not created by the operator, the copyrights of third parties are respected. In particular, third-party content is marked as such. Should you nevertheless become aware of a copyright infringement, please inform us accordingly. Upon becoming aware of legal violations, we will remove such content immediately.'
        },
        disputeResolution: {
          title: 'Information on Online Dispute Resolution',
          content: 'The European Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr/\n\nOur email address can be found above in the imprint.\n\nWe are not willing or obligated to participate in dispute resolution procedures before a consumer arbitration board.\n\n**Information according to § 36 VSBG**\n\nAccording to § 36 VSBG (Consumer Dispute Resolution Act), we inform you that we are neither willing nor obligated to participate in a dispute resolution procedure before a consumer arbitration board.\n\nIf you are nevertheless interested in out-of-court dispute resolution, you can contact one of the general consumer arbitration boards, for example:\n\nGeneral Consumer Arbitration Board of the Center for Arbitration e.V.\nStraßburger Straße 8\n77694 Kehl am Rhein\nWebsite: www.verbraucher-schlichter.de'
        }
      }
    }
  },

  // Email Templates
  email: {
    downloadKey: {
      subject: 'Your privacv download key',
      title: 'Thank you for your purchase!',
      subtitle: {
        '24h': 'Your 24-hour pass is ready',
        '7d': 'Your 7-day pass is ready', 
        '30d': 'Your 30-day pass is ready'
      },
      sections: {
        personalKey: {
          title: 'Your Personal Download Key',
          description: 'We\'ve generated a secure download key for your privacv pass. This approach ensures your privacy by keeping your resume data on your device while allowing unlimited downloads during your pass period.'
        },
        keyContainer: {
          label: 'Your Download Key:',
          copyButton: 'Copy Key',
          selectButton: 'Select Text',
          tip: '💡 Tip: You can also manually select and copy the key above'
        },
        instructions: {
          title: 'How to use your download key:',
          steps: [
            'Return to <strong>privacv.com</strong>',
            'Click <strong>"Export PDF"</strong> → <strong>"Have a download key?"</strong>',
            'Paste your download key and click <strong>"Verify"</strong>',
            'Download your professional resume as many times as needed!'
          ]
        },
        whyKeys: {
          title: 'Why are we sending you keys?',
          description: 'Your resume data never leaves your browser - it stays 100% private on your device. Our keys let you unlock downloads without us storing any of your personal information.'
        },
        validity: {
          '24h': '⏰ This key expires in 24 hours. Save it somewhere safe!',
          '7d': '⏰ This key expires in 7 days. Save it somewhere safe!',
          '30d': '⏰ This key expires in 30 days. Save it somewhere safe!'
        },
        footer: {
          tagline: 'privacv - Privacy-first resume builder',
          subtitle: 'Made with ❤️ for job seekers worldwide'
        }
      },
      plainText: {
        intro: 'Thank you for your privacv purchase!',
        passReady: {
          '24h': 'Your 24-hour pass is ready.',
          '7d': 'Your 7-day pass is ready.',
          '30d': 'Your 30-day pass is ready.'
        },
        keyLabel: 'DOWNLOAD KEY:',
        instructions: [
          'How to use:',
          '1. Go to privacv.com',
          '2. Click "Export PDF" → "Have a download key?"',
          '3. Paste your download key and verify',
          '4. Download unlimited PDFs during your pass period'
        ],
        whyKeys: 'Why download keys? Your resume data stays 100% private on your device. We never store your personal information.',
        validity: {
          '24h': 'This key expires in 24 hours.',
          '7d': 'This key expires in 7 days.',
          '30d': 'This key expires in 30 days.'
        },
        support: 'Need help? Reply to this email.',
        signature: 'privacv - Privacy-first resume builder'
      }
    }
  }
}; 